import { useState, useEffect } from 'react';
import { authenticatedApi } from '../../Api/api';
import env from '../../config/env';
import { IDocument } from '../../models/document';
import { isAdaptedDocument } from '../../utils/documentAdapter';

interface UseDocumentTokenOptions {
  document: IDocument | null;
  getTokenUrl: (documentId: number) => string;
  downloadType?: 'view' | 'download';
  enabled?: boolean;
}

interface UseDocumentTokenResult {
  viewUrl: string | null;
  downloadUrl: string | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Hook to manage document token fetching and URL generation
 * Used by DocumentViewer and DocumentPreview components
 */
export function useDocumentToken({
  document,
  getTokenUrl,
  enabled = true
}: UseDocumentTokenOptions): UseDocumentTokenResult {
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchToken = async () => {
    if (!document || !enabled) {
      setViewUrl(null);
      setDownloadUrl(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // NEW: Check if this is an adapted document with custom URL functions
      if (isAdaptedDocument(document) && document._getViewUrl) {
        const viewUrlStr = await document._getViewUrl();
        const downloadUrlStr = document._getDownloadUrl
          ? await document._getDownloadUrl()
          : viewUrlStr;

        setViewUrl(viewUrlStr);
        setDownloadUrl(downloadUrlStr);
        setLoading(false);
        return;
      }

      // Standard flow for regular IDocument
      const documentId = document.id;
      const tokenUrl = getTokenUrl(documentId);

      // Check if this is a delivery proof (special case)
      const isDeliveryProof = (document as any).isDeliveryProof;

      // Fetch token via the provided endpoint
      const response = await authenticatedApi.get(tokenUrl);
      const fetchedToken = response.data?.documentToken;

      if (!fetchedToken) {
        throw new Error('Impossible de récupérer le token de téléchargement');
      }


      // Build URLs with the token
      let baseUrl: string;
      let viewUrlStr: string;
      let downloadUrlStr: string;

      if (isDeliveryProof) {
        // For delivery proof, use the special endpoint with documentDownloadToken
        const savId = (document as any).savId;
        baseUrl = `${env.PUBLIC_API_URL}/sav-v2/${savId}/deliveryProof/download`;
        viewUrlStr = `${baseUrl}?downloadType=view&documentDownloadToken=${fetchedToken}`;
        downloadUrlStr = `${baseUrl}?downloadType=download&documentDownloadToken=${fetchedToken}`;
      } else {
        // For regular documents, use the standard endpoint
        baseUrl = `${env.PUBLIC_API_URL}/documents/${documentId}/download`;
        viewUrlStr = `${baseUrl}?downloadType=view&documentDownloadToken=${fetchedToken}`;
        downloadUrlStr = `${baseUrl}?downloadType=download&documentDownloadToken=${fetchedToken}`;
      }

      setViewUrl(viewUrlStr);
      setDownloadUrl(downloadUrlStr);
    } catch (err) {
      console.error('Erreur lors du chargement du document:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement du document');
      setViewUrl(null);
      setDownloadUrl(null);
    } finally {
      setLoading(false);
    }
  };

  // Load document URL when component mounts or document changes
  useEffect(() => {
    fetchToken();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.id, enabled]);

  return {
    viewUrl,
    downloadUrl,
    loading,
    error,
    refresh: fetchToken
  };
}

/**
 * Utility function to determine file type from filename
 */
export function getFileType(fileName: string): 'image' | 'pdf' | 'other' {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')) return 'image';
  if (ext === 'pdf') return 'pdf';
  return 'other';
}

/**
 * Utility function to get display name from document
 */
export function getFileName(doc: IDocument): string {
  return doc.fileName || doc.originalName || doc.label || `Document #${doc.id}`;
}